/**
 * Left sidebar — brand header, the Solutions / Development / Administration
 * sections, footer with the "view as role" picker and the signed-in user card.
 *
 * **Solutions show what was granted** (ADR-0048): each entry is an agent, and
 * it appears only when `/me` lists that agent — through a grant on the agent
 * or its Solution, or by being the Administrator. A Solution with no visible
 * agent is not shown at all. Roadmap items open an inert `ModulePlaceholder`
 * (ADR-0004) and carry a "Soon" badge, so they read as planned rather than
 * broken.
 *
 * **Development** holds the building surfaces: Integrations, Data browser and
 * Collections are Developer+; Projects is all roles (ADR-0018). The heading
 * shows whenever at least one child does. **Administration** holds user
 * management, Solution access and the tenant audit log (Administrator only —
 * EP.1802 Q-1, ADR-0045, ADR-0068), so for a `User` or a Developer the whole
 * section is absent rather than present and disappointing. Validation's
 * support tickets are its group's last entry, for Administrators (ADR-0069).
 * Placement is not permission: the nav only hides; the routes and the API
 * re-check every request (each role's actions are written out in
 * `backend/tests/compliance/test_role_capability_sets.py`).
 *
 * **It collapses to an icon rail, not to nothing.** The control that reopens a
 * sidebar has to live somewhere; parking it in the header means the header
 * carries a button for one rare state forever. A rail keeps navigation reachable
 * while giving the canvas back most of the width, and the toggle stays exactly
 * where it was. The rail is **one icon per section** — each Solution group,
 * Development, Administration — and each opens a labelled fly-out of that
 * section's pages, the same pages in the same order as at full width. So it
 * stays a few icons long however many pages there are; the section holding the
 * current page is marked, and the person's initials at the foot open their
 * profile and sign-out.
 */

import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Divider,
  Group,
  Image,
  Menu,
  NavLink,
  Select,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  IconBooks,
  IconBrandAws,
  IconBrandOffice,
  IconCloud,
  IconCode,
  IconDatabase,
  IconFiles,
  IconFolderSearch,
  IconFolders,
  IconKey,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconLogout,
  IconPlugConnected,
  IconShieldHalf,
  IconUserCircle,
  IconUserShield,
  IconUsersGroup,
} from "@tabler/icons-react";
import { useMsal } from "@azure/msal-react";
import { Fragment } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useMe } from "@/features/users/useMe";
import { useEffectiveRoles, useRolePreview } from "@/features/auth/RolePreviewContext";
import { endSession } from "@/features/auth/session";
import { initials } from "@/shared/ui/initials";
import {
  isActive,
  type ModuleEntry,
  type SolutionGroup,
  visibleSolutionGroups,
} from "./solutionNav";

/** The data browsers. The icons show in the rail's fly-out only; at full width
 *  they are rows of the Data browser disclosure. */
const BROWSERS: ModuleEntry[] = [
  { path: "/browse/veeva", label: "Veeva Vault", icon: <IconFolderSearch size={18} /> },
  { path: "/browse/blob", label: "Azure Blob Storage", icon: <IconCloud size={18} /> },
  { path: "/browse/database", label: "Database", icon: <IconDatabase size={18} /> },
  { path: "/browse/cara", label: "Generis CARA", icon: <IconFiles size={18} /> },
  { path: "/browse/s3", label: "AWS S3", icon: <IconBrandAws size={18} /> },
  { path: "/browse/sharepoint", label: "SharePoint", icon: <IconBrandOffice size={18} /> },
];

/** The navy the mark is drawn on. Not a theme colour — the theme's scale is
 *  derived from the older two-tone blue, and this is the logo file's own value.
 *  One place, so the header and the rail cannot drift apart. */
const BRAND_NAVY = "#051F45";

function SoonBadge() {
  return (
    <Badge size="xs" variant="light" color="gray">
      Soon
    </Badge>
  );
}

export function NavbarPanel({
  collapsed = false,
  onToggle,
}: {
  collapsed?: boolean;
  onToggle?: () => void;
}) {
  const { pathname } = useLocation();
  const { data } = useMe();
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const { previewRole, setPreviewRole } = useRolePreview();

  const realRoles = data?.roles ?? [];
  const isAdmin = realRoles.includes("administrator");
  // Effective roles honour an admin's "view as role" preview; for everyone
  // else these equal the real roles. Nav visibility uses the effective set so
  // an admin previewing as User sees exactly what a User sees.
  const effectiveRoles = useEffectiveRoles(realRoles);
  // Administration surfaces are Developer+: configuring connections, browsing
  // a connected system directly, user management, the audit log. Every route
  // behind them is RBAC-gated server-side too.
  const isDeveloperPlus =
    effectiveRoles.includes("developer") || effectiveRoles.includes("administrator");
  const showIntegration = isDeveloperPlus;
  // Collections sit beside the other building surfaces and share their gate.
  const showCollections = isDeveloperPlus;
  // Projects are everyone's (ADR-0018) — moving them under Development is
  // placement, not permission. What a User actually *sees* inside is still
  // decided per resource by ownership + grants (ADR-0015), so an empty list is
  // a normal state, not a permission failure.
  const showProjects = true;
  // Agents come from grants (ADR-0048), not from the role; an
  // Administrator's preview keeps their own (they reach every agent).
  const groups = visibleSolutionGroups(
    data?.agents ?? [],
    isDeveloperPlus,
    effectiveRoles.includes("administrator"),
    data?.deployment_solutions,
  );
  const showDevelopment = showIntegration || showProjects || showCollections;
  // Administration surfaces: user management, Solution access and the tenant
  // audit log are the Administrator's (EP.1802 Q-1 — the trail is read-only
  // for everyone, and only Administrators read the whole of it; ADR-0068 —
  // only Administrators grant a Solution). RBAC-gated server-side too.
  const showUserMgmt = effectiveRoles.includes("administrator");
  const showSolutionAccess = effectiveRoles.includes("administrator");
  const showAudit = effectiveRoles.includes("administrator");
  // The release validation settings are not here: they open from the gear on
  // the Release validation runs page (ADR-0064).

  // The server ends the session first (ADR-0046): after that the token is
  // refused everywhere, then Microsoft's sign-out runs.
  const handleSignOut = async () => {
    try {
      if (accounts[0]) instance.setActiveAccount(accounts[0]);
      await endSession(instance, "sign_out", { interactive: true });
    } finally {
      navigate("/login", { replace: true });
    }
  };

  if (collapsed) {
    return (
      <Rail
        pathname={pathname}
        onToggle={onToggle}
        showProjects={showProjects}
        showIntegration={showIntegration}
        showCollections={showCollections}
        groups={groups}
        showUserMgmt={showUserMgmt}
        showSolutionAccess={showSolutionAccess}
        showAudit={showAudit}
        displayName={data?.display_name ?? ""}
        role={primaryRole(data?.roles ?? [])}
        onSignOut={() => void handleSignOut()}
      />
    );
  }

  return (
    <Stack h="100%" gap={0}>
      <BrandHeader onToggle={onToggle} />

      <Box p="md" className="daq-no-scrollbar" style={{ flex: 1, overflowY: "auto" }}>
        <Text size="xs" tt="uppercase" fw={600} c="dimmed" mb={6}>
          Solutions
        </Text>
        <Stack gap={2}>
          {/* Closed by default so the sidebar opens tidy; the group holding
              the current page opens itself so you never lose your place. */}
          {groups.map((group) => (
            <NavLink
              key={group.label}
              label={group.label}
              leftSection={group.icon}
              defaultOpened={group.entries.some((entry) => isActive(pathname, entry))}
              variant="light"
            >
              {group.entries.map((entry) => (
                <NavLink
                  key={entry.path}
                  component={RouterLink}
                  to={entry.path}
                  label={entry.label}
                  leftSection={entry.icon}
                  rightSection={entry.soon ? <SoonBadge /> : undefined}
                  active={isActive(pathname, entry)}
                  variant="light"
                />
              ))}
            </NavLink>
          ))}
        </Stack>

        {showDevelopment && (
          <>
            <Text size="xs" tt="uppercase" fw={600} c="dimmed" mt="lg" mb={6}>
              Development
            </Text>
            <Stack gap={2}>
              {/* One entry, not three. Connected / Roles / Add connection are
                  tabs inside the hub: they are three views of one subject, and
                  as separate nav rows they made the sidebar carry the hub's
                  internal structure. Each keeps its own URL, so a deep link
                  still lands on the right tab. */}
              {showIntegration && (
                <NavLink
                  component={RouterLink}
                  to="/integration/connections"
                  label="Integrations"
                  leftSection={<IconPlugConnected size={18} />}
                  active={pathname.startsWith("/integration")}
                  variant="light"
                />
              )}
              {showProjects && (
                <NavLink
                  component={RouterLink}
                  to="/projects"
                  label="Projects"
                  leftSection={<IconFolders size={18} />}
                  active={pathname.startsWith("/projects")}
                  variant="light"
                />
              )}
              {showIntegration && (
                <NavLink
                  label="Data browser"
                  leftSection={<IconFolderSearch size={18} />}
                  defaultOpened={pathname.startsWith("/browse")}
                  variant="light"
                >
                  {BROWSERS.map((b) => (
                    <NavLink
                      key={b.path}
                      component={RouterLink}
                      to={b.path}
                      label={b.label}
                      active={pathname.startsWith(b.path)}
                      variant="light"
                    />
                  ))}
                </NavLink>
              )}
              {showCollections && (
                <NavLink
                  component={RouterLink}
                  to="/development/collections"
                  label="Collections"
                  leftSection={<IconBooks size={18} />}
                  active={pathname.startsWith("/development/collections")}
                  variant="light"
                />
              )}
            </Stack>
          </>
        )}

        {(showUserMgmt || showSolutionAccess || showAudit) && (
          <>
            <Text size="xs" tt="uppercase" fw={600} c="dimmed" mt="lg" mb={6}>
              Administration
            </Text>
            <Stack gap={2}>
              {showUserMgmt && (
                <NavLink
                  component={RouterLink}
                  to="/admin/users"
                  label="User management"
                  leftSection={<IconUsersGroup size={18} />}
                  active={pathname.startsWith("/admin/users")}
                  variant="light"
                />
              )}
              {showSolutionAccess && (
                <NavLink
                  component={RouterLink}
                  to="/admin/solution-access"
                  label="Solution access"
                  leftSection={<IconKey size={18} />}
                  active={pathname.startsWith("/admin/solution-access")}
                  variant="light"
                />
              )}
              {showAudit && (
                <NavLink
                  component={RouterLink}
                  to="/admin/audit"
                  label="Audit log"
                  leftSection={<IconShieldHalf size={18} />}
                  active={pathname.startsWith("/admin/audit")}
                  variant="light"
                />
              )}
            </Stack>
          </>
        )}
      </Box>

      <Divider />

      <Stack gap="xs" p="md">
        <Text size="xs" tt="uppercase" fw={600} c="dimmed">
          View as role
        </Text>
        <Select
          value={capitalise(previewRole ?? primaryRole(realRoles))}
          data={["Administrator", "Developer", "User"]}
          onChange={(v) => {
            if (!v) return;
            const lower = v.toLowerCase();
            // Selecting the admin's own role clears the preview.
            setPreviewRole(lower === "administrator" ? null : lower);
          }}
          disabled={!isAdmin}
          allowDeselect={false}
          size="xs"
          aria-label={isAdmin ? "View as role (preview)" : "View as role (admins only)"}
        />
        {previewRole && previewRole !== "administrator" && (
          <Text size="xs" c="orange.7">
            Previewing as {previewRole}, pick Administrator to exit.
          </Text>
        )}

        {data && (
          <UserCard
            displayName={data.display_name}
            role={primaryRole(data.roles)}
            onSignOut={() => void handleSignOut()}
          />
        )}
      </Stack>
    </Stack>
  );
}

function BrandHeader({ onToggle }: { onToggle?: () => void }) {
  return (
    <Group
      gap="sm"
      align="center"
      wrap="nowrap"
      p="md"
      style={{
        // The mark's own navy, not the old indigo gradient: the white wordmark
        // is drawn for this colour, and a near-miss behind a logo reads as a
        // printing error rather than a gradient.
        background: BRAND_NAVY,
        color: "white",
      }}
    >
      <UnstyledButton
        component={RouterLink}
        to="/"
        aria-label="DAQUMA home"
        style={{ flex: 1, minWidth: 0, color: "inherit" }}
      >
        {/* The 2026 brand wordmark: white DAQUMA, blush-pink Q, on the navy. */}
        <Image
          src="/logo-daquma-white.png"
          alt="DAQUMA"
          h={30}
          w="auto"
          fit="contain"
          style={{ display: "block" }}
        />
      </UnstyledButton>
      {onToggle && (
        <Tooltip label="Collapse navigation" position="right">
          <ActionIcon
            variant="subtle"
            color="gray.2"
            onClick={onToggle}
            aria-label="Collapse navigation"
          >
            <IconLayoutSidebarLeftCollapse size={18} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
}

/** The collapsed sidebar: the same destinations, icon-only, labels in tooltips. */
/** One page in a rail fly-out, and the sub-heading it sits under there. */
interface RailEntry extends ModuleEntry {
  heading?: string;
}

/** One rail icon: a section of the full-width sidebar. */
interface RailGroup {
  key: string;
  label: string;
  icon: React.ReactNode;
  entries: RailEntry[];
}

/** The current page's entry in a fly-out, drawn like a lit NavLink. */
const RAIL_ITEM_ACTIVE: React.CSSProperties = {
  backgroundColor: "var(--mantine-color-brand-light)",
  color: "var(--mantine-color-brand-light-color)",
};

function Rail({
  pathname,
  onToggle,
  showProjects,
  showIntegration,
  showCollections,
  groups,
  showUserMgmt,
  showSolutionAccess,
  showAudit,
  displayName,
  role,
  onSignOut,
}: {
  pathname: string;
  onToggle?: () => void;
  showProjects: boolean;
  showIntegration: boolean;
  showCollections: boolean;
  groups: SolutionGroup[];
  showUserMgmt: boolean;
  showSolutionAccess: boolean;
  showAudit: boolean;
  displayName: string;
  role: string;
  onSignOut: () => void;
}) {
  // The sections of the full-width sidebar, with the same pages in the same
  // order: a rail icon per section, not per page.
  const railGroups: RailGroup[] = [
    ...groups.map((g) => ({ key: g.solution, label: g.label, icon: g.icon, entries: g.entries })),
    {
      key: "development",
      label: "Development",
      icon: <IconCode size={20} />,
      entries: [
        ...(showIntegration
          ? [
              {
                label: "Integrations",
                path: "/integration/connections",
                also: ["/integration"],
                icon: <IconPlugConnected size={18} />,
              },
            ]
          : []),
        ...(showProjects
          ? [{ label: "Projects", path: "/projects", icon: <IconFolders size={18} /> }]
          : []),
        ...(showIntegration ? BROWSERS.map((b) => ({ ...b, heading: "Data browser" })) : []),
        ...(showCollections
          ? [
              {
                label: "Collections",
                path: "/development/collections",
                icon: <IconBooks size={18} />,
              },
            ]
          : []),
      ],
    },
    {
      key: "administration",
      label: "Administration",
      icon: <IconUserShield size={20} />,
      entries: [
        ...(showUserMgmt
          ? [{ label: "User management", path: "/admin/users", icon: <IconUsersGroup size={18} /> }]
          : []),
        ...(showSolutionAccess
          ? [
              {
                label: "Solution access",
                path: "/admin/solution-access",
                icon: <IconKey size={18} />,
              },
            ]
          : []),
        ...(showAudit
          ? [{ label: "Audit log", path: "/admin/audit", icon: <IconShieldHalf size={18} /> }]
          : []),
      ],
    },
  ].filter((g) => g.entries.length > 0);

  // Longest match wins, so a page and a page nested under it never both light;
  // `also` lets a hub's entry match any of its tabs.
  const activePath = railGroups
    .flatMap((g) => g.entries)
    .filter((e) => isActive(pathname, e))
    .sort((a, b) => b.path.length - a.path.length)[0]?.path;

  return (
    <Stack h="100%" gap={0} align="center">
      <Stack gap={4} align="center" py="sm">
        <UnstyledButton component={RouterLink} to="/" aria-label="DAQUMA home">
          <Image src="/logo-daquma-badge.png" alt="DAQUMA" h={32} w={32} fit="contain" />
        </UnstyledButton>
        {onToggle && (
          <Tooltip label="Expand navigation" position="right">
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={onToggle}
              aria-label="Expand navigation"
            >
              <IconLayoutSidebarLeftExpand size={18} />
            </ActionIcon>
          </Tooltip>
        )}
      </Stack>

      <Divider w="100%" />

      <Stack
        gap={6}
        align="center"
        py="sm"
        w="100%"
        className="daq-no-scrollbar"
        style={{ flex: 1, overflowY: "auto" }}
      >
        {railGroups.map((g) => (
          <RailGroupMenu key={g.key} group={g} activePath={activePath} />
        ))}
      </Stack>

      <Divider w="100%" />

      <Box py="sm">
        <Menu position="right-end" offset={14} width={230} shadow="md" withinPortal>
          <Menu.Target>
            <UnstyledButton aria-label="Your account">
              <Avatar size={32} color="brand" radius="xl">
                {initials(displayName)}
              </Avatar>
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            <Box px="sm" py={6}>
              <Text size="sm" fw={500} truncate>
                {displayName}
              </Text>
              <Text size="xs" c="dimmed" tt="capitalize">
                {role}
              </Text>
            </Box>
            <Menu.Divider />
            <Menu.Item component={RouterLink} to="/me" leftSection={<IconUserCircle size={16} />}>
              My profile
            </Menu.Item>
            <Menu.Item leftSection={<IconLogout size={16} />} onClick={onSignOut}>
              Sign out
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Box>
    </Stack>
  );
}

/** A rail icon and its fly-out: the section's name, then its pages (under a
 *  sub-heading where the full-width sidebar nests them). Opens on hover or
 *  click; the arrow keys move through it. */
function RailGroupMenu({ group, activePath }: { group: RailGroup; activePath?: string }) {
  const active = group.entries.some((e) => e.path === activePath);
  return (
    <Box pos="relative" w="100%" style={{ display: "flex", justifyContent: "center" }}>
      {/* Where you are: a short bar against the rail's edge. */}
      {active && (
        <Box
          aria-hidden
          pos="absolute"
          left={0}
          top="50%"
          w={3}
          h={20}
          bg="var(--mantine-color-brand-filled)"
          style={{ transform: "translateY(-50%)", borderRadius: "0 3px 3px 0" }}
        />
      )}
      <Menu
        trigger="click-hover"
        openDelay={60}
        closeDelay={180}
        position="right-start"
        offset={14}
        width={250}
        shadow="md"
        withinPortal
      >
        <Menu.Target>
          <ActionIcon
            size="lg"
            variant={active ? "light" : "subtle"}
            color={active ? "brand" : "gray"}
            aria-label={group.label}
          >
            {group.icon}
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>{group.label}</Menu.Label>
          {group.entries.map((e, i) => {
            const prev = group.entries[i - 1];
            const here = e.path === activePath;
            return (
              <Fragment key={e.path}>
                {e.heading && e.heading !== prev?.heading && <Menu.Label>{e.heading}</Menu.Label>}
                {!e.heading && prev?.heading && <Menu.Divider />}
                <Menu.Item
                  component={RouterLink}
                  to={e.path}
                  leftSection={e.icon}
                  rightSection={e.soon ? <SoonBadge /> : undefined}
                  aria-current={here ? "page" : undefined}
                  style={here ? RAIL_ITEM_ACTIVE : undefined}
                >
                  {e.label}
                </Menu.Item>
              </Fragment>
            );
          })}
        </Menu.Dropdown>
      </Menu>
    </Box>
  );
}

interface UserCardProps {
  displayName: string;
  role: string;
  onSignOut: () => void;
}

function UserCard({ displayName, role, onSignOut }: UserCardProps) {
  return (
    <Group gap="xs" wrap="nowrap" align="center" mt={4} justify="space-between">
      {/* The way to the full profile (`/me`); Home is the logo (ADR-0070). */}
      <UnstyledButton
        component={RouterLink}
        to="/me"
        aria-label="Your profile"
        style={{ minWidth: 0, color: "inherit" }}
      >
        <Group gap="xs" wrap="nowrap" align="center" style={{ minWidth: 0 }}>
          <Avatar size="sm" color="brand" radius="xl">
            {initials(displayName)}
          </Avatar>
          <Box style={{ minWidth: 0 }}>
            <Text size="sm" fw={500} truncate>
              {displayName}
            </Text>
            <Text size="xs" c="dimmed" tt="capitalize">
              {role}
            </Text>
          </Box>
        </Group>
      </UnstyledButton>
      <Group gap={2} wrap="nowrap">
        {/* Theme and Settings live in the header, beside the bell — one copy of
            each, always on screen. Sign out stays with the person it signs out. */}
        <Tooltip label="Sign out">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={onSignOut}
            aria-label="Sign out"
          >
            <IconLogout size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Group>
  );
}

function capitalise(role: string): string {
  if (!role) return "";
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function primaryRole(roles: string[]): string {
  // Priority: administrator > developer > user > the first whatever we got.
  if (roles.includes("administrator")) return "administrator";
  if (roles.includes("developer")) return "developer";
  if (roles.includes("user")) return "user";
  return roles[0] ?? "-";
}
